import React, { useMemo } from "react";
import { ApplicationDetailContextProps } from "../../../../application/service/smart-apply/application-detail/application-detail-service";
import { ContentsHeader, PrimaryButton } from "@graffer-inc/console-design-kit";
import { ApplicationStatusTag } from "../../../molecules/status-tag";
import { useModal } from "../../../../utils/custom-hooks";
import { StatusUpdateModal } from "../../../organisms/smart-apply/modals/status-update-modal";
import { RevertStatusModal } from "../../../organisms/smart-apply/modals/revert-status-modal";
import { ApplyStatus } from "../../../../application/domain/application/application-entity";
import dayjs from "../../../../utils/date-utils";
import { useAuthContextV2 } from "../../../../auth-state";

type ApplicationDetailHeaderType = Pick<
  ApplicationDetailContextProps,
  "application" | "procedure"
> & {
  updateApplyStatus: (
    appl: ApplyStatus,
    skipNotification: boolean,
    message?: string,
  ) => Promise<void>;
  onRevertStatusToWip: (internalComment: string) => Promise<void>;
  validRemovalTask?: {
    unixtime: number;
    id: string;
  };
  children?: React.ReactNode;
};

const StatusChangeButton: React.FC<
  Pick<ApplicationDetailContextProps, "application"> & {
    statusModal: ReturnType<typeof useModal>;
    revertModal: ReturnType<typeof useModal>;
  }
> = ({ application, statusModal, revertModal }) => {
  if (!application) return undefined;
  if (application.isDone())
    return (
      <PrimaryButton
        icon={"RepeatIcon"}
        onClick={revertModal.open}
        text={"ステータスを戻す"}
      />
    );
  if (application.changableStatus().length === 0) {
    return <span> この申請はステータス変更ができません</span>;
  }
  return (
    <PrimaryButton
      icon={"RepeatIcon"}
      onClick={statusModal.open}
      text={"ステータスを更新"}
      data-for-rpa-id={"update-status-detail"}
    />
  );
};

export const ApplicationDetailHeader: React.FC<ApplicationDetailHeaderType> = ({
  application,
  procedure,
  children,
  validRemovalTask,
  updateApplyStatus,
  onRevertStatusToWip,
}) => {
  const statusModal = useModal(false);
  const revertModal = useModal(false);
  const authState = useAuthContextV2();
  const canReadApplicationRemovalTask = useMemo(() => {
    return authState.auth.checkGroupPermissionWithoutTarget([
      "applications.removal_tasks_read",
    ]);
  }, [authState]);
  return (
    <>
      {validRemovalTask && (
        <div
          className="columns m-0 p-2 "
          style={{ backgroundColor: "#DE3226" }}
        >
          <div className="column ">
            <div className="columns is-centered is-size-5 has-text-weight-bold has-text-white	">
              この申請は
              {dayjs.unix(validRemovalTask.unixtime).format("YYYY/MM/DD")}
              に削除予定です。
            </div>
            <div className="columns is-centered is-size-7 has-text-white">
              {canReadApplicationRemovalTask ? (
                <>申請削除タスクID: {validRemovalTask.id}</>
              ) : (
                <>
                  削除を中止するには、グループ管理者か特権管理者にお問い合わせください。(申請削除タスクID:{" "}
                  {validRemovalTask.id})
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <ContentsHeader
        title={`申請詳細：${application?.formattedId}`}
        caption={[
          application ? (
            <ApplicationStatusTag status={application.status} />
          ) : undefined,
          <p key={"applicationDetailId"}>申請番号: {application?.id}</p>,
        ]}
        buttons={[
          [
            <StatusChangeButton
              key={"applicationDetailStatusChangeButton"}
              application={application}
              statusModal={statusModal}
              revertModal={revertModal}
            />,
          ],
        ]}
      >
        {children}
      </ContentsHeader>
      {!!application && (
        <>
          <StatusUpdateModal
            isActive={statusModal.isActive}
            app={application}
            onClose={statusModal.close}
            onUpdateStatus={updateApplyStatus}
          />
          <RevertStatusModal
            isActive={revertModal.isActive}
            application={application}
            procedureIsArchived={procedure?.isArchived() ?? false}
            removalIsScheduled={!!validRemovalTask}
            onClose={revertModal.close}
            onRevertStatusToWip={onRevertStatusToWip}
          />
        </>
      )}
    </>
  );
};
